import styled, { css } from 'styled-components';
import { DEFAULT_COLORS } from 'components/core/colors';

export const ShoppingCartHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0px 20px 0px;
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

export const ShippingOptionWrapper = styled.div<{
  $isChecked?: boolean;
  $disabled?: boolean;
}>`
  padding: 12px;
  background: ${({ $isChecked }) =>
    $isChecked ? DEFAULT_COLORS.surface.selected : DEFAULT_COLORS.surface.dim};
  border-radius: 6px;
  border: 1px solid
    ${({ $isChecked }) =>
      $isChecked
        ? DEFAULT_COLORS.accent.primaryAlt
        : DEFAULT_COLORS.surface.checkedBorder};
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    border-color: ${DEFAULT_COLORS.accent.primaryAlt};
    background: ${DEFAULT_COLORS.surface.selected};
  }
`;

export const ShippingOptionCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

export const ShippingOptionContent = styled.div`
  flex: 1;
  cursor: pointer;
`;

export const ShippingOptionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${DEFAULT_COLORS.ink.darkest};
  margin-bottom: 2px;
`;

export const ShippingOptionHint = styled.div`
  font-size: 12px;
  color: ${DEFAULT_COLORS.sky.darkest};
  line-height: 1.4;
`;

export const LoadingSpinnerWrapper = styled.div`
  padding: 15px;
`;

export const EmptyCartButtonLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${DEFAULT_COLORS.black};
  cursor: pointer;
  margin-left: 4px;
`;

export const ShoppingCartContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
  }
`;

export const ShoppingCartSection = styled.div`
  overflow: unset;
  flex: 1;
  max-width: 72%;

  > :first-child {
    margin: 0 0 10px 0;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 100%;
    order: 2;
  }
`;

export const CheckoutSection = styled.div`
  width: 25%;
  box-sizing: border-box;
  padding: 20px;
  background: ${DEFAULT_COLORS.white};
  border-radius: ${({ theme }) => theme.borderRadius};
  height: max-content;
  align-self: flex-start;
  position: sticky;
  top: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
    order: 1;
    position: static;
    margin-bottom: 10px;
  }
`;

export const SubTotalTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 400;
  margin-bottom: 2%;
  color: ${DEFAULT_COLORS.grey63};
`;

export const SubTotalPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 5%;
`;

export const SubTotalWithDiscount = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 10px;
`;

export const TotalSavings = styled.div`
  font-size: 0.875rem;
  color: #649942;
  font-weight: 500;
`;

export const LineItemHeader = styled.div<{
  $isMultiShip?: boolean;
}>`
  box-sizing: border-box;
  display: grid;

  grid-template-columns: ${({ $isMultiShip }) =>
    $isMultiShip
      ? '0.5fr 2fr 0.5fr 0.5fr 0.5fr'
      : '1.3fr 3fr 1fr 1fr 1.2fr 1fr'};
  width: 100%;
  background: ${DEFAULT_COLORS.white};
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius};

  gap: 0 15px;
  > div:first-of-type {
    margin-left: 15px;
  }
`;

export const LineItemHeaderTitle = styled.div<{ $textAlign?: string }>`
  font-size: 0.8rem;
  font-weight: 400;
  padding: 10px 0px;
  color: ${DEFAULT_COLORS.grey63};
  text-align: ${({ $textAlign }) => $textAlign || 'left'};
`;

export const LineItemContainer = styled(LineItemHeader)<{
  disabled: boolean;
}>`
  margin-top: 10px;
  background: ${({ disabled }) =>
    disabled ? DEFAULT_COLORS.grey5 : DEFAULT_COLORS.white};
  > .tooltip {
    width: 300px;
    font-size: 0.8rem;
  }
  padding: 5px 0px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    padding: 16px;
  }
`;

export const LineItemThumbnail = styled.div<{ $isMultiShip?: boolean }>`
  position: relative;
  width: 80px;
  height: 80px;
  margin: ${$isMultiShip =>
    $isMultiShip
      ? '10px 0 20px 30px !important'
      : '10px 0 10px 40px !important'};

  cursor: pointer;
  left: 0;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`;

export const LineItemDescription = styled.div<{ $isMultiShip?: boolean }>`
  font-weight: 500;

  display: flex;
  flex-direction: column;
  gap: 5px;

  margin-left: 0;
  ${({ $isMultiShip }) => $isMultiShip && 'grid-column: 2 / 5;'}
`;

export const DiscountPercentage = styled.p<{
  hasDiscount: boolean;
}>`
  margin: 0;
  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 1rem 0 0 0;
  }

  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ hasDiscount, theme }) =>
    hasDiscount ? theme.palette.text.success : DEFAULT_COLORS.error};
  background-color: ${({ hasDiscount }) =>
    hasDiscount ? DEFAULT_COLORS.successGreenBg : 'none'};
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
`;

export const LineItemAttributes = styled.div`
  display: flex;
  align-items: center;
`;

export const LineItemQuantity = styled.div``;

export const LineItemTotal = styled.div<{
  $isMultiShip?: boolean;
}>`
  display: flex;
  font-weight: 500;
  white-space: nowrap;

  ${$isMultiShip =>
    $isMultiShip
      ? css`
          display: flex;
          font-weight: 500;
          gap: 5px;
          font-size: 1rem;
        `
      : css`
          flex-direction: column;
        `}
`;

export const LineItemControlsGrid = styled.div`
  grid-column: 2 / span 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

export const MultiShipColumnHeaders = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 2fr 0.5fr 0.5fr 0.5fr;
  gap: 12px;
  align-items: center;
`;

export const LineItemTotalWithoutDiscount = styled.div`
  font-weight: 300;
  font-size: 0.8rem;
  text-decoration: line-through;
`;

export const EmptyCartMessage = styled.div`
  width: 100%;
  font-size: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const CartHeaderButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const LineItemMultiShipHeader = styled.div`
  text-align: right;
`;

export const LineItemTotalLabel = styled.span`
  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`;

export const CheckoutError = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.error};
  margin-top: 10px;
  line-height: 1.4;
`;

export const PriceColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CheckoutErrorHeading = styled.h3`
  margin: 0 0 4px 0;
  font-size: 14px;
`;

export const CheckoutErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

export const SubTotal = styled.span`
  font-size: 14px;
  color: ${DEFAULT_COLORS.grey60};
  text-decoration: line-through;
`;

export const NetTotal = styled.span<{ $hasDiscount?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $hasDiscount }) =>
    $hasDiscount ? DEFAULT_COLORS.primary : DEFAULT_COLORS.ink.darkest};
`;
